<template lang="pug">
    .uploader
        .header
          IQToolMiddlePaneHeader(:header="$t(`${baseTranslationPath}.header`)"
            :title="$t(`${baseTranslationPath}.title`)"
            @goToListPage="navigateAway()")
          .button-container
            aiq-button(size="small" class="btn" @click="navigateAway()") {{ $t(`global.cancel`) }}
            aiq-button(type="primary" size="small" :disabled="!canSubmit" class="btn" @click="saveForm()") {{ $t(`global.save`) }}
        EditArea.edit-area
          template(#content)
            label {{ $t(`${baseTranslationPath}.file_name`) }}
             aiq-input(v-model="fileName" :placeholder="$t(`${baseTranslationPath}.file_name`)")
            .file-upload
              .upload-info {{ $t(`${baseTranslationPath}.upload_your_document`) }}
              FileUploader(
                  :fileList="fileList",
                  @getFileList="getFileList",
                  upload_tips="(PDF - MAX 25MB)",
                  acceptedFileType="application/pdf"
                  :supported_files="['application/pdf']"
              )
            .endpoints-section
              .endpoints-header {{ $t(`${baseTranslationPath}.endpoints_header`) }}
              .endpoint-row(v-for="option in endpointOptions" :key="option.key")
                label {{ $t(`${baseTranslationPath}.${option.labelKey}`) }}
                aiq-cascader(:model-value="endpoints[option.key]"
                  expand-trigger="hover"
                  :placeholder="$t(`${baseTranslationPath}.select_endpoint`)"
                  size="small"
                  :options="processedEndpoints"
                  :show-all-levels="false"
                  @change="onEndpointSelect(option.key, $event)")
            .tip-area
                .tip-header
                  fa-icon(icon="fa-solid fa-lightbulb-on" class="tip-icon")
                  span {{ $t(`${baseTranslationPath}.tips`) }}
                .tip-content(v-html="getTipContent")
</template>
<script>
import IQToolMiddlePaneHeader from '@/pages/IQTools/Partials/IQToolMiddlePaneHeader/IQToolMiddlePaneHeader.vue';
import { createNamespacedHelpers } from 'vuex';
import get from 'lodash/get';
import EditArea from '@/components/EditArea.vue';
import FileUploader from '@/components/FileUtil/FileUploader.vue';

const { mapGetters } = createNamespacedHelpers('settings');

// Each box maps a workflow role to an endpoint id the backend needs to compile
// the form's dialogs. Keys match ai-manager's form.endpoints contract.
const ENDPOINT_OPTIONS = [
  { key: 'online_user', labelKey: 'user_endpoint' },
  { key: 'account', labelKey: 'account_endpoint' },
  { key: 'transactions', labelKey: 'transactions_endpoint' },
];

export default {
  name: 'UploadForms',
  components: {
    IQToolMiddlePaneHeader,
    EditArea,
    FileUploader,
  },
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.upload',
      fileList: [],
      fileName: '',
      endpointOptions: ENDPOINT_OPTIONS,
      // { online_user: [domainId, endpointId], account: [...], transactions: [...] }
      endpoints: {},
    };
  },
  mounted() {
    this.$store.dispatch('settings/getAPISettings');
  },
  computed: {
    ...mapGetters(['endpointsByDomain']),
    // Cascader options: domain (API) group -> its endpoints as children.
    // The endpoint id is the value the backend stores on form.endpoints.
    processedEndpoints() {
      return this.endpointsByDomain
        .filter(domain => (domain.children || []).length > 0)
        .map(domain => ({
          value: domain.id,
          label: domain.name,
          children: domain.children.map(endpoint => ({
            value: endpoint.id,
            label: endpoint.name,
          })),
        }));
    },
    getTipContent() {
      let content = this.$t(`${this.baseTranslationPath}.upload_tip`);
      content = content.split('\n');
      content = content.map((item) => `<li>${item}</li>`);
      return `<ol>${content.join('')}</ol>`;
    },
    canSubmit() {
      if (!this.fileName || this.fileList.length === 0) {
        return false;
      }
      // All three endpoints must be picked before the backend can compile dialogs.
      return this.endpointOptions.every(option => get(this.endpoints, `${option.key}.1`));
    },
  },
  methods: {
    onEndpointSelect(key, selection) {
      this.endpoints = { ...this.endpoints, [key]: selection };
    },
    navigateAway() {
      // editor is contained by a route
      const id = get(this, '$route.params.id');
      if (id && this.$route.matched.length > 1) {
        // get the parent path
        const currentPath = this.$route.path;
        const parentPath = currentPath.substring(
          0,
          currentPath.lastIndexOf('/'),
        );
        // get parant name if exist
        const name = get(this.$route, 'meta.parentName');
        // move to the parent
        this.$router.push(name ? { name } : parentPath);
      }
    },
    getFileList(fileList) {
      this.fileList = fileList;
      if (!this.fileName) {
        this.fileName = get(this.fileList, '0.name', '');
      }
    },
    async saveForm() {
      const id = get(this.fileList, '0.file_id');
      // Reduce each [domainId, endpointId] selection to the endpoint id.
      const endpoints = this.endpointOptions.reduce((acc, option) => {
        acc[option.key] = get(this.endpoints, `${option.key}.1`);
        return acc;
      }, {});
      const payload = {
        title: this.fileName,
        external_file_id: id,
        endpoints,
      };
      try {
        await this.$store.dispatch('agentForms/createFormFromFile', payload);
        this.$aiq.notify.success('Form saved successfully');
        this.navigateAway();
      } catch (e) {
        this.$aiq.notify.error('Error while saving form');
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import "./../../../styles/aiq-variables.scss";

:deep(ol), :deep(ul) {
    margin: unset;
    padding: unset;
    padding-left: 1.5rem;
}

.file-upload {
  padding-top: 10px;
}

.endpoints-section {
  padding-top: 20px;
}

.endpoints-header {
  font-size: 14px;
  font-weight: 600;
  padding-bottom: 10px;
}

.endpoint-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 12px;

  label {
    font-size: 12px;
    font-weight: 400;
  }

  :deep(.el-cascader) {
    width: 100%;
  }
}

:deep(ol li),
:deep(ul li) {
  margin: 0;
  padding: 5px 0;
}

:deep(ol li) {
  list-style: decimal;
}

:deep(ul li) {
  list-style: disc;
}
.edit-area {
  margin-top:30px;
}

.tip-header {
  display: flex;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  padding-bottom: 10px;
}
.tip-area {
  margin-top: 20px;
  font-size: 12px;
  font-weight: 400;
  padding: 10px;
  background-color: $aiq-bgc-list-item;
}
.uploader {
  padding: 30px;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn {
  height: 40px;
  width: 70px;
}
.button-container {
  display: flex;
  gap: 5px;
  justify-content: flex-end;
}
</style>
